/** @prettier */

import {useLoaderData, useParams, Link, useNavigate} from 'react-router-dom';
import {Grid, Box, Menu, MenuItem, Tabs, Tab} from '@mui/material';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import type {LoaderFunctionArgs} from 'react-router-dom';
import {useState, useEffect} from 'react';
import {useTranslation} from 'react-i18next';

//API import
import {
  getCompositionsSitesBySiteID,
  getWeatherCurrent,
  getSettingUser,
} from '../../API/API_Collection';
//interface import
import * as apiIF from '../../Type/api';
//Style import
import '../../css/common/common.css';

import {iconBlack} from '../../define';
import {getLanguageForAPIQuery} from '../../functions/weatherAPI';

import {checkUnitExistence} from '../../functions/checkUnitExistence';
import type {unitExistence} from '../../functions/checkUnitExistence';
import {checkSiteInfoBillingStatus} from '../../functions/checkBillingStatus';

type LoaderReturnType = {
  /** ユーザがサイトで付与されているの最上位権限 */
  siteAuthority: string;
  /** サイトに登録されている全GWの課金状態。全GWが無課金状態の場合True */
  isAllGWNoPaid: boolean;
  /** 登録空調機の有無 */
  hasAircon: boolean;
  /** 登録Q-tonの有無 */
  hasQton: boolean;
  /** 登録Hyozan室外機の有無 */
  hasHyozanOU: boolean;
  /** 登録Hyozan室内機の有無 */
  hasHyozanIU: boolean;
  /** 登録Hydrolution PROの有無 */
  hasChiller: boolean;
  /** 登録HCCVQの有無 */
  hasHCCVQ: boolean;
  /** サイトの状態値の有無 */
  hasStates: boolean;
  /** weatherAPI用の言語 */
  weatherAPILanguage: apiIF.reqParameters.getWeatherCurrent['language'];
  /** 未払い状態のゲートウェイの有無 */
  billingStatus: apiIF.resParameters.siteInformations['billing-status'];
};
export async function loader({
  params,
}: LoaderFunctionArgs): Promise<LoaderReturnType> {
  /** 応答データ */
  let contents: LoaderReturnType = {
    siteAuthority: 'none',
    billingStatus: 'unpaid',
    isAllGWNoPaid: true,
    hasAircon: false,
    hasQton: false,
    hasHyozanOU: false,
    hasHyozanIU: false,
    hasChiller: false,
    hasHCCVQ: false,
    hasStates: false,
    weatherAPILanguage: 'en',
  };

  //パスパラメータを取得できない場合
  if (params.siteID === undefined) {
    return contents;
  }

  try {
    const promises = [];
    //ユニットの登録状況を取得
    promises.push(checkUnitExistence(params.siteID, true));
    //サイトの課金状態を取得
    promises.push(getCompositionsSitesBySiteID(params.siteID));
    //ユーザ情報を取得
    promises.push(getSettingUser());
    const response = await Promise.all(promises);

    /** ユニットの登録状況 */
    const unitExistence = response[0] as unitExistence;
    /** サイトの情報 */
    const siteInfo = response[1] as apiIF.res.getCompositionsSitesBySiteID;
    /** ユーザの情報 */
    const userSettingInfo = response[2] as apiIF.res.getSettingUser;

    //空調機とModbus、熱源機の有無を確認
    if (unitExistence) {
      contents.hasAircon = unitExistence.hasAirconIU as boolean;
      contents.hasQton = unitExistence.hasQton;
      contents.hasHyozanOU = unitExistence.hasHyozanOU;
      contents.hasHyozanIU = unitExistence.hasHyozanIU;
      contents.hasChiller = unitExistence.hasHydrolutionPro;
      contents.hasHCCVQ = unitExistence.hasHCCVQ;
    }
    //サイトの権限を確認
    contents.siteAuthority = siteInfo['site-authority'];
    //ゲートウェイの課金情報の取得
    contents.billingStatus = siteInfo['billing-status'];
    //課金状態のGWの有無を確認
    if (checkSiteInfoBillingStatus(siteInfo['billing-status'])) {
      contents.isAllGWNoPaid = false;
    }
    //weatherAPI用の言語を取得
    contents.weatherAPILanguage = getLanguageForAPIQuery(
      userSettingInfo.language
    );
    return contents;
  } catch {
    return contents;
  }
}

/**
 * サイト詳細のメインの画面のサブヘッダ
 * @param props props
 * @returns JSX.Element
 */
export const MainSiteDetail = (props: {
  fullWidth?: boolean;
  shouldDisplayWeather?: boolean;
  location?: apiIF.resParameters.siteInformations['location'];
}) => {
  const siteInfo = useLoaderData() as LoaderReturnType;
  const navigate = useNavigate();
  const trans = useTranslation().t; //翻訳関数

  //パスパラメータ取得
  const {siteID} = useParams();

  //prettier-ignore
  //サイト設定メニュー
  const [siteSettingMenu, setSiteSettingMenu] = useState<null | HTMLElement>(null);
  const siteSettingMenuOpen = Boolean(siteSettingMenu);
  //prettier-ignore
  //サービス情報メニュー
  const [serviceInfoMenu, setServiceInfoMenu] = useState<null | HTMLElement>(null);
  const serviceInfoMenuOpen = Boolean(serviceInfoMenu);
  //スケジュール設定メニュー
  const [scheduleMenu, setScheduleMenu] = useState<null | HTMLElement>(null);
  const scheduleMenuOpen = Boolean(scheduleMenu);
  //管理メニュー
  const [manageMenu, setManageMenu] = useState<null | HTMLElement>(null);
  const manageMenuOpen = Boolean(manageMenu);
  //prettier-ignore
  //天気
  const [weather, setWeather] = useState<apiIF.res.getWeatherCurrent | null>(null);

  //天気を取得
  useEffect(() => {
    if (props.location !== undefined) {
      getWeatherCurrent({
        location: props.location,
        language: siteInfo.weatherAPILanguage,
      }).then(data => {
        setWeather(data);
      });
    }
  }, [props.location, siteInfo.weatherAPILanguage]);

  type MenuItemType = {
    /** 遷移先 */
    link: string;
    /** ID */
    id: string;
    /** 適用するクラス名 */
    className?: string;
    /** ラベル名用翻訳キー */
    transKeyForLabel: string;
    /** 無効にするか */
    disabled?: boolean;
  };
  /**
   * サイト設定メニューを開いた時の処理
   * @param event
   */
  const siteSettingMenuClick = (event: React.MouseEvent<HTMLDivElement>) => {
    setSiteSettingMenu(event.currentTarget);
  };
  /**
   * サイト設定メニューを閉じたときの処理
   */
  const siteSettingMenuClose = () => {
    setSiteSettingMenu(null);
  };
  /**
   * サイト設定メニュー項目
   * @param props props
   * @returns 項目
   */
  const siteSettingMenuItems = ({
    siteID,
    hasAircon,
    hasHyozanIU,
    hasHCCVQ,
  }: {
    /** サイトID */
    siteID: string;
    /** 登録空調機の有無 */
    hasAircon: boolean;
    /** 登録Hyozan室内機の有無 */
    hasHyozanIU: boolean;
    /** 登録HCCVQの有無 */
    hasHCCVQ: boolean;
  }) => {
    /** メニュー項目 */
    const items: MenuItemType[] = [];

    //ゲートウェイ設定
    items.push({
      link: `/pages/my-site/site-detail/${siteID}/gw-setting`,
      id: 'mv-linkGateWay',
      transKeyForLabel: 'MainSiteDetail.menuList.siteSetting.item.gateway',
    });
    //グループ設定
    //空調機の登録がある場合表示
    if (hasAircon) {
      items.push({
        link: `/pages/my-site/site-detail/${siteID}/floor-detail`,
        id: 'mv-linkGroup',
        transKeyForLabel: 'MainSiteDetail.menuList.siteSetting.item.group',
      });
    }
    //Hyozan用グループ設定
    //Hyozan室内機の登録がある場合表示
    if (hasHyozanIU) {
      items.push({
        link: `/pages/my-site/site-detail/${siteID}/hyozan-group-registration`,
        id: 'mv-linkHyozanGroupSetting',
        transKeyForLabel:
          'MainSiteDetail.menuList.serviceInfo.item.hyozanGroupSetting',
      });
    }
    //Modbus用グループ設定
    //Modbusの登録がある場合表示
    if (hasHCCVQ) {
      items.push({
        link: `/pages/my-site/site-detail/${siteID}/modbus-group-registration`,
        id: 'mv-linkGroupSetting',
        transKeyForLabel:
          'MainSiteDetail.menuList.serviceInfo.item.hccvqGroupSetting',
      });
    }
    //サイト設定
    items.push({
      link: `/pages/my-site/site-detail/${siteID}/site-setting`,
      id: 'mv-linkSite',
      transKeyForLabel: 'MainSiteDetail.menuList.siteSetting.item.site',
    });
    //セキュリィティ設定
    items.push({
      link: `/pages/my-site/site-detail/${siteID}/security`,
      id: 'mv-linkSecurity',
      transKeyForLabel: 'MainSiteDetail.menuList.siteSetting.item.security',
    });

    return items;
  };

  /**
   * サービス情報メニューを開いた時の処理
   * @param event
   */
  const serviceInfoMenuClick = (event: React.MouseEvent<HTMLDivElement>) => {
    setServiceInfoMenu(event.currentTarget);
  };
  /**
   * サービス情報メニューを閉じたときの処理
   */
  const serviceInfoMenuClose = () => {
    setServiceInfoMenu(null);
  };
  /**
   * サービス情報メニュー項目
   * @param props props
   * @returns 項目
   */
  const serviceInfoMenuItems = ({
    siteID,
    hasQton,
    hasHyozanOU,
    hasChiller,
    hasHCCVQ,
    isAllGWNoPaid,
  }: {
    /** サイトID */
    siteID: string;
    /** 登録Q-tonの有無 */
    hasQton: boolean;
    /** 登録Hyozan室外機の有無 */
    hasHyozanOU: boolean;
    /** 登録Hydrolution PROの有無 */
    hasChiller: boolean;
    /** 登録HCCVQの有無 */
    hasHCCVQ: boolean;
    /** 全GWの課金状態 */
    isAllGWNoPaid: boolean;
  }) => {
    /** メニュー項目 */
    const items: MenuItemType[] = [];

    //日報一覧
    //Q-ton or Hyozan室外機 or Hydrolution PRO or Modbusの登録があり、熱源機の権限がある場合表示
    //GWが全て無課金の場合無効化
    if (hasQton || hasHyozanOU || hasChiller || hasHCCVQ) {
      items.push({
        link: `/pages/my-site/site-detail/${siteID}/daily-report`,
        id: 'mv-linkDailyReportData',
        transKeyForLabel:
          'MainSiteDetail.menuList.serviceInfo.item.dailyReportData',
        disabled: isAllGWNoPaid,
      });
    }
    //日報要求
    //Q-ton or Hyozan室外機 or Hydrolution PROの登録があり、熱源機の権限がある場合表示
    //GWが全て無課金の場合無効化
    if (hasQton || hasHyozanOU || hasChiller) {
      items.push({
        link: `/pages/my-site/site-detail/${siteID}/request-report`,
        id: 'mv-linkTransferRequest',
        transKeyForLabel:
          'MainSiteDetail.menuList.serviceInfo.item.transferRequest',
        disabled: isAllGWNoPaid,
      });
    }
    //定期レポート
    //Q-ton or Hydrolution PROの登録があり、Admin権限の場合表示
    //GWが全て無課金の場合無効化
    if (siteInfo.siteAuthority === 'admin') {
      if (hasQton || hasChiller) {
        items.push({
          link: `/pages/my-site/site-detail/${siteID}/periodic-report`,
          id: 'mv-linkReport',
          transKeyForLabel: 'MainSiteDetail.menuList.serviceInfo.item.report',
          disabled: isAllGWNoPaid,
        });
      }
    }
    //異常不調履歴
    //運転データ履歴
    //空調機の登録がある場合表示があり、空調機または熱源機の権限がある場合表示
    //GWが全て無課金の場合無効化
    items.push({
      link: `/pages/my-site/site-detail/${siteID}/condition-history`,
      id: 'mv-linkConditionHistory',
      transKeyForLabel:
        'MainSiteDetail.menuList.serviceInfo.item.conditionHistory',
      disabled: isAllGWNoPaid,
    });
    items.push({
      link: `/pages/my-site/site-detail/${siteID}/operation-data-history`,
      id: 'mv-linkOperationDataHistory',
      transKeyForLabel:
        'MainSiteDetail.menuList.serviceInfo.item.operationDataHistory',
      disabled: isAllGWNoPaid,
    });
    //サービス履歴
    //Admin権限の場合表示
    if (siteInfo.siteAuthority === 'admin') {
      items.push({
        link: `/pages/my-site/site-detail/${siteID}/service-history`,
        id: 'mv-linkServiceHistory',
        transKeyForLabel:
          'MainSiteDetail.menuList.serviceInfo.item.serviceHistory',
        disabled: false,
      });
    }

    return items;
  };

  /**
   * スケジュールメニューを開いた時の処理
   * @param event
   */
  const scheduleMenuClick = (event: React.MouseEvent<HTMLDivElement>) => {
    setScheduleMenu(event.currentTarget);
  };
  /**
   * スケジュールメニューを閉じたときの処理
   */
  const scheduleMenuClose = () => {
    setScheduleMenu(null);
  };
  /**
   * スケジュールメニュー項目
   * @param siteID サイトID
   * @returns 項目
   */
  const scheduleMenuItems = (siteID: string) => {
    /** メニュー項目 */
    const items: MenuItemType[] = [];

    //当日スケジュール
    items.push({
      link: `/pages/my-site/site-detail/${siteID}/schedule/today`,
      id: 'mv-schedule-today',
      transKeyForLabel: 'MainSiteDetail.menuList.manage.item.todaySchedule',
    });
    //年間カレンダー設定
    items.push({
      link: `/pages/my-site/site-detail/${siteID}/schedule`,
      id: 'mv-schedule',
      transKeyForLabel: 'MainSiteDetail.menuList.manage.item.calender',
    });
    //特定日スケジュール
    items.push({
      link: `/pages/my-site/site-detail/${siteID}/schedule/daily`,
      id: 'mv-schedule-daily',
      transKeyForLabel: 'MainSiteDetail.menuList.manage.item.dailySchedule',
    });
    return items;
  };

  /**
   * 管理メニューを開いた時の処理
   * @param event
   */
  const manageMenuClick = (event: React.MouseEvent<HTMLDivElement>) => {
    setManageMenu(event.currentTarget);
  };
  /**
   * 管理メニューを閉じたときの処理
   */
  const manageMenuClose = () => {
    setManageMenu(null);
  };
  /**
   * 管理メニュー項目
   * @param siteID サイトID
   * @returns 項目
   */
  const manageMenuItems = (siteID: string) => {
    /** メニュー項目 */
    const items: MenuItemType[] = [];

    //省エネ設定
    items.push({
      link: `/pages/my-site/site-detail/${siteID}/saving-energy`,
      id: 'mv-linkEnergySavingControl',
      transKeyForLabel: 'MainSiteDetail.menuList.manage.item.energySaving',
    });
    //デマンド設定
    items.push({
      link: `/pages/my-site/site-detail/${siteID}/demand`,
      id: 'mv-linkDemandControl',
      transKeyForLabel: 'MainSiteDetail.menuList.manage.item.demand',
    });
    //ロジック設定
    items.push({
      link: `/pages/my-site/site-detail/${siteID}/logic`,
      id: 'mv-linkLogic',
      transKeyForLabel: 'MainSiteDetail.menuList.manage.item.logic',
    });
    return items;
  };

  //パスパラメータを取得できない場合
  if (siteID === undefined) {
    return <></>;
  }
  return (
    <Grid
      container
      className={
        props.fullWidth
          ? 'mySiteMainContents__subHeader--siteDetail--fullWidth'
          : 'mySiteMainContents__subHeader--siteDetail--hasSubContents'
      }
      alignItems="center">
      {/** サブメニュー */}
      <Grid item lg xs={12}>
        <Tabs
          aria-label="Sub menu"
          scrollButtons={false}
          value={false}
          className="mySiteMainContents__subHeader--siteDetail__menuBox"
          variant="scrollable"
          visibleScrollbar>
          {/** 外観 */}
          <Tab
            label={
              <Link
                className="mySiteMainContents__subHeader--siteDetail__menuBox--overview"
                to={`/pages/my-site/site-detail/${siteID}/state`}
                id="mv-linkOverview">
                {trans('MainSiteDetail.linkTxt.overview')}
              </Link>
            }
          />
          {/** サイト設定 */}
          {/** admin権限場合表示 */}
          {siteInfo.siteAuthority === 'admin' && (
            <Tab
              id="mv-boxSiteSetting"
              aria-controls="Site setting tab"
              icon={
                <img
                  className="mySiteMainContents__subHeader--siteDetail__menuBox__arrowIcon"
                  src={iconBlack.CHEVRON_DOWN.src}
                  alt={`${trans(
                    `MainSiteDetail.image.alternativeText.${iconBlack.CHEVRON_DOWN.alt}`
                  )}`}
                />
              }
              iconPosition="end"
              label={
                <Box
                  component="span"
                  className="mySiteMainContents__subHeader--siteDetail__menuBox--siteSetting">
                  {trans('MainSiteDetail.menuList.siteSetting.title')}
                </Box>
              }
              onClick={siteSettingMenuClick}
            />
          )}
          <Menu
            anchorEl={siteSettingMenu}
            open={siteSettingMenuOpen}
            onClose={siteSettingMenuClose}>
            {siteSettingMenuItems({
              siteID: siteID,
              hasAircon: siteInfo.hasAircon,
              hasHyozanIU: siteInfo.hasHyozanIU,
              hasHCCVQ: siteInfo.hasHCCVQ,
            }).map(item => (
              <MenuItem
                key={item.id}
                id={item.id}
                onClick={() => {
                  navigate(item.link);
                  siteSettingMenuClose();
                }}>
                {trans(item.transKeyForLabel)}
              </MenuItem>
            ))}
          </Menu>
          {/** サービス情報 */}
          {/** admin権限場合または、空調機の登録がある場合表示 */}
          {(siteInfo.siteAuthority === 'admin' ||
            siteInfo.hasAircon ||
            siteInfo.hasQton ||
            siteInfo.hasHyozanOU ||
            siteInfo.hasHyozanIU ||
            siteInfo.hasChiller ||
            siteInfo.hasHCCVQ) && (
            <Tab
              id="mv-qtonUnitInfoMenu"
              aria-controls="Service information tab"
              icon={
                <img
                  className="mySiteMainContents__subHeader--siteDetail__menuBox__arrowIcon"
                  src={iconBlack.CHEVRON_DOWN.src}
                  alt={`${trans(
                    `MainSiteDetail.image.alternativeText.${iconBlack.CHEVRON_DOWN.alt}`
                  )}`}
                />
              }
              iconPosition="end"
              label={
                <Box
                  component="span"
                  className="mySiteMainContents__subHeader--siteDetail__menuBox--serviceInfo">
                  {trans('MainSiteDetail.menuList.serviceInfo.title')}
                </Box>
              }
              onClick={serviceInfoMenuClick}
            />
          )}
          <Menu
            anchorEl={serviceInfoMenu}
            open={serviceInfoMenuOpen}
            onClose={serviceInfoMenuClose}>
            {serviceInfoMenuItems({
              siteID: siteID,
              hasQton: siteInfo.hasQton,
              hasHyozanOU: siteInfo.hasHyozanOU,
              hasChiller: siteInfo.hasChiller,
              hasHCCVQ: siteInfo.hasHCCVQ,
              isAllGWNoPaid: siteInfo.isAllGWNoPaid,
            }).map(item => (
              <MenuItem
                key={item.id}
                id={item.id}
                disabled={item.disabled}
                onClick={() => {
                  navigate(item.link);
                  serviceInfoMenuClose();
                }}>
                {trans(item.transKeyForLabel)}
              </MenuItem>
            ))}
          </Menu>
          {/** スケジュール */}
          {/** 空調機の登録があり、空調機の権限がAdmin or Managerの場合（サイトの最上位権限がManager以上）は表示 */}
          {siteInfo.hasAircon &&
            (siteInfo.siteAuthority === 'admin' ||
              siteInfo.siteAuthority === 'manager') && (
              <Tab
                id="mv-scheduleMenu"
                aria-controls="schedule setting tab"
                icon={
                  <img
                    className="mySiteMainContents__subHeader--siteDetail__menuBox__arrowIcon"
                    src={iconBlack.CHEVRON_DOWN.src}
                    alt={`${trans(
                      `MainSiteDetail.image.alternativeText.${iconBlack.CHEVRON_DOWN.alt}`
                    )}`}
                  />
                }
                iconPosition="end"
                label={
                  <Box
                    component="span"
                    className="mySiteMainContents__subHeader--siteDetail__menuBox--schedule">
                    {trans('MainSiteDetail.menuList.schedule.title')}
                  </Box>
                }
                onClick={scheduleMenuClick}
              />
            )}
          <Menu
            anchorEl={scheduleMenu}
            open={scheduleMenuOpen}
            onClose={scheduleMenuClose}>
            {scheduleMenuItems(siteID).map(item => (
              <MenuItem
                key={item.id}
                id={item.id}
                onClick={() => {
                  navigate(item.link);
                  scheduleMenuClose();
                }}>
                {trans(item.transKeyForLabel)}
              </MenuItem>
            ))}
          </Menu>
          {/** 管理 */}
          {/** 空調機の登録があり、Admin権限の場合表示 */}
          {siteInfo.hasAircon && siteInfo.siteAuthority === 'admin' && (
            <Tab
              id="mv-manageMenu"
              aria-controls="manage information tab"
              icon={
                <img
                  className="mySiteMainContents__subHeader--siteDetail__menuBox__arrowIcon"
                  src={iconBlack.CHEVRON_DOWN.src}
                  alt={`${trans(
                    `MainSiteDetail.image.alternativeText.${iconBlack.CHEVRON_DOWN.alt}`
                  )}`}
                />
              }
              iconPosition="end"
              label={
                <Box
                  component="span"
                  className="mySiteMainContents__subHeader--siteDetail__menuBox--manage">
                  {trans('MainSiteDetail.menuList.manage.title')}
                </Box>
              }
              onClick={manageMenuClick}
            />
          )}
          <Menu
            anchorEl={manageMenu}
            open={manageMenuOpen}
            onClose={manageMenuClose}>
            {manageMenuItems(siteID).map(item => (
              <MenuItem
                key={item.id}
                id={item.id}
                onClick={() => {
                  navigate(item.link);
                  manageMenuClose();
                }}>
                {trans(item.transKeyForLabel)}
              </MenuItem>
            ))}
          </Menu>
          {/** 課金 */}
          {/** admin権限場合表示 */}
          {siteInfo.siteAuthority === 'admin' && (
            <Tab
              label={
                <Link
                  to={`/pages/my-site/site-detail/${siteID}/billing-setting`}
                  className="mySiteMainContents__subHeader--siteDetail__menuBox--billing"
                  id="mv-linkBilling">
                  {trans('MainSiteDetail.linkTxt.billing')}
                </Link>
              }
              icon={
                siteInfo.billingStatus === 'unpaid' ||
                siteInfo.billingStatus === 'anyUnpaid' ? (
                  <ErrorOutlineRoundedIcon color="secondary" fontSize="small" />
                ) : undefined
              }
              iconPosition="end"
            />
          )}
        </Tabs>
      </Grid>
      {/** 現在の天気 */}
      {props.shouldDisplayWeather && weather !== null && (
        //横幅1366pxより小さい画面では非表示
        <Grid
          item
          lg="auto"
          className="mySiteMainContents__subHeader--siteDetail__weather">
          <Grid container columnSpacing={1} alignItems="center">
            {/** アイコン */}
            <Grid item xs="auto">
              <img
                className="mySiteMainContents__subHeader--siteDetail__weather__icon"
                src={weather.current.condition.icon.replace(
                  '//cdn.weatherapi.com',
                  `${process.env.PUBLIC_URL}/img/mysite/icon`
                )}
                alt={`${trans(
                  'MainSiteDetail.image.alternativeText.currentWeatherIcon'
                )}`}
                title={weather.current.condition.text}
              />
            </Grid>
            {/** 温度 */}
            <Grid item xs="auto">
              <Box className="mySiteMainContents__subHeader--siteDetail__weather__text">
                {`${weather.current.temp_c}${trans(
                  'MainSiteDetail.weather.unit.temp'
                )}`}
              </Box>
            </Grid>
          </Grid>
        </Grid>
      )}
    </Grid>
  );
};
export default MainSiteDetail;
